/**
 * @file: SelectCloserPage.tsx
 * @copyright: (c) 2019-2020 sichuan zhichetech co., ltd.
 *
 * Full-screen picker for the closer on a product-manifest item.
 * Modeled on SelectMemberPage but fetches imperatively (useEffect +
 * useState) instead of via the Suspense-based useMemberList — the
 * latter's thrown promise never unsuspends when this screen is pushed
 * from inside ProductAdoptionConfirmationPage, even though the network
 * call succeeds. Carries a search field so a workshop with many staff
 * stays usable.
 *
 * The BE (changeProductManifestItemCloser foundation function) only
 * accepts closer.type ∈ {ServiceAgent, Technicians}; the picker is
 * filtered to those roles client-side, but managers/etc are dropped
 * silently rather than rendered-then-rejected.
 */

import { MemberCell } from '@euler/app/flows/common/components/MemberCell';
import Euler from '@euler/components/layout';
import { OrgUserRoleHelper } from '@euler/functions/OrgUserRoleHelper';
import { useResponsiveContext } from '@euler/functions/responsive/ResponsiveContext';
import { wrapNavigatorScreen } from '@euler/functions/wrapNavigatorScreen';
import { OrgMember } from '@euler/model/entity';
import { OrgUserRoleType } from '@euler/model/enum';
import { useServiceFactory } from '@euler/services/factory';
import { useEffect, useMemo, useState } from 'react';
import { ActivityIndicator, FlatList, Text, TextInput } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';

const kCloserRoles: OrgUserRoleType[] = [
  OrgUserRoleType.ServiceAgents,
  OrgUserRoleType.Technicians,
];

export const SelectCloserPage = wrapNavigatorScreen(
  ({
    onSelect,
  }: {
    title?: string;
    onSelect?: (member: OrgMember) => void;
  }) => {
    const { rpx } = useResponsiveContext();
    const insets = useSafeAreaInsets();
    const { storeService } = useServiceFactory();
    const [all, setAll] = useState<OrgMember[] | undefined>(undefined);
    const [keyword, setKeyword] = useState('');

    useEffect(() => {
      let cancelled = false;
      void storeService
        .listMembers({})
        .then(list => {
          if (cancelled) return;
          const filtered = list.filter(m =>
            new OrgUserRoleHelper(m.role).hasAnyRole(...kCloserRoles),
          );
          setAll(filtered);
        })
        .catch(e => {
          console.warn('[select-closer] listMembers failed', e);
          if (!cancelled) setAll([]);
        });
      return () => {
        cancelled = true;
      };
    }, [storeService]);

    const members = useMemo(() => {
      if (!all) return undefined;
      const q = keyword.trim();
      if (!q) return all;
      return all.filter(m => m.name?.includes(q) || m.mobile?.includes(q));
    }, [all, keyword]);

    if (members == null) {
      return (
        <Euler.View flex={1} alignItems="center" justifyContent="center">
          <ActivityIndicator size="small" />
        </Euler.View>
      );
    }

    return (
      <Euler.View flex={1}>
        <Euler.View
          paddingHorizontal={rpx(15)}
          pt={rpx(10)}
          pb={rpx(10)}
          backgroundColor="#fff"
        >
          <TextInput
            value={keyword}
            onChangeText={setKeyword}
            placeholder="搜索姓名或手机号"
            placeholderTextColor="#9aa0ac"
            returnKeyType="search"
            css={`
              background-color: #f3f4f6;
              border-radius: ${rpx(8)}px;
              padding: ${rpx(8)}px ${rpx(12)}px;
              font-size: ${rpx(14)}px;
              color: #1b1f2a;
            `}
          />
        </Euler.View>
        <FlatList
          data={members}
          keyExtractor={x => String(x.id)}
          keyboardShouldPersistTaps="handled"
          contentContainerStyle={{
            paddingLeft: rpx(12),
            paddingRight: rpx(12),
            paddingBottom: insets.bottom + rpx(15),
          }}
          ListEmptyComponent={
            <Euler.View alignItems="center" pt={rpx(40)}>
              <Text style={{ color: '#9aa0ac', fontSize: rpx(13) }}>
                没有匹配的员工
              </Text>
            </Euler.View>
          }
          renderItem={({ item, index }) => (
            <MemberCell
              member={item}
              first={index === 0}
              last={index === members.length - 1}
              onPress={member => onSelect?.(member)}
              isRoleLabelVisible
            />
          )}
        />
      </Euler.View>
    );
  },
  ({ route }) => ({
    title: route.params.title ?? '选择成交人',
    cardStyle: { backgroundColor: '#fff' },
  }),
);
